<template>
  <div
    :class="[
      'inline-notice',
      `inline-notice-${variant}`,
      `inline-notice-${size}`,
      `inline-notice-align-${align}`
    ]"
  >
    <i :class="computedIcon" class="inline-notice-icon" />
    <span class="inline-notice-text">
      <slot />
    </span>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'info',
    validator: (v) => ['info', 'warning', 'danger', 'success', 'muted'].includes(v)
  },
  icon: {
    type: String,
    default: null
  },
  size: {
    type: String,
    default: 'sm',
    validator: (v) => ['sm', 'md'].includes(v)
  },
  /**
   * Alignement vertical de l'icône par rapport au texte.
   *
   * `center` (défaut) convient au cas courant : une phrase d'une ou deux
   * lignes, rien d'autre. Dès que le contenu est PLUS HAUT que son texte —
   * une phrase qui porte un bouton d'action, par exemple —, l'icône centrée
   * sur toute cette hauteur se retrouve à flotter en face de rien : elle est
   * alors au milieu du bloc, et non devant la phrase qu'elle annonce.
   *
   * `baseline` la pose sur la ligne de base de la PREMIÈRE ligne de texte.
   * C'est le seul réglage juste dans les deux cas de figure : que le bouton
   * tienne au bout de la phrase ou qu'il repasse dessous quand la largeur
   * manque, l'icône ne bouge pas de devant le premier mot.
   */
  align: {
    type: String,
    default: 'center',
    validator: (v) => ['center', 'baseline'].includes(v)
  }
})

const defaultIcons = {
  info: 'fas fa-info-circle',
  warning: 'fas fa-exclamation-triangle',
  danger: 'fas fa-exclamation-circle',
  success: 'fas fa-check-circle',
  muted: 'fas fa-info-circle'
}

const computedIcon = computed(() => props.icon || defaultIcons[props.variant])
</script>

<style scoped>
.inline-notice {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

/* Cf. la prop `align`. L'icône garde son `line-height: 1` de Font Awesome :
   aligner des lignes de base rend l'interligne de la boîte sans effet sur la
   position du glyphe, il n'y a donc rien à corriger ici. */
.inline-notice-align-baseline {
  align-items: baseline;
}

.inline-notice-icon {
  flex-shrink: 0;
}

.inline-notice-text {
  line-height: var(--line-height-normal);
}

/* Sizes */
.inline-notice-sm {
  font-size: var(--font-size-sm);
}

.inline-notice-sm .inline-notice-icon {
  font-size: var(--font-size-sm);
}

.inline-notice-md {
  font-size: var(--font-size-base);
}

.inline-notice-md .inline-notice-icon {
  font-size: var(--font-size-base);
}

/* Variants */
.inline-notice-info {
  color: var(--color-info-dark);
}

.inline-notice-info .inline-notice-icon {
  color: var(--color-info);
}

.inline-notice-warning {
  color: var(--color-warning-dark);
}

.inline-notice-warning .inline-notice-icon {
  color: var(--color-warning);
}

.inline-notice-danger {
  color: var(--color-danger-dark);
}

.inline-notice-danger .inline-notice-icon {
  color: var(--color-danger);
}

.inline-notice-success {
  color: var(--color-success-dark);
}

.inline-notice-success .inline-notice-icon {
  color: var(--color-success);
}

.inline-notice-muted {
  color: var(--color-text-muted);
}

.inline-notice-muted .inline-notice-icon {
  color: var(--color-text-muted);
}
</style>
